import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, MessageSquareOff } from "lucide-react";
import type { ConversationSessionItem } from "@/types/user-conversation";
import type { PaginationMeta } from "@/types/common";
import type { UsePaginationReturn } from "@/hooks/use-pagination";

interface Props {
  data: ConversationSessionItem[];
  meta: PaginationMeta;
  pagination: UsePaginationReturn;
  onOpenSession: (session: ConversationSessionItem) => void;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("ko-KR");
}

function SessionTypeBadge({ type }: { type: string }) {
  const isStory = type === "STORY";
  return (
    <Badge variant={isStory ? "default" : "secondary"}>
      {isStory ? "스토리" : "일반 챗"}
    </Badge>
  );
}

export function ConversationSessionTable({
  data,
  meta,
  pagination,
  onOpenSession,
}: Props) {
  if (data.length === 0) {
    return (
      <div className="rounded-lg border p-12 text-center space-y-3">
        <MessageSquareOff className="mx-auto h-10 w-10 text-muted-foreground" />
        <p className="text-muted-foreground">대화 내역이 없습니다.</p>
      </div>
    );
  }

  const rangeStart = (meta.currentPage - 1) * meta.limit + 1;
  const rangeEnd = Math.min(meta.currentPage * meta.limit, meta.totalCount);

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>제목</TableHead>
              <TableHead className="w-[90px]">유형</TableHead>
              <TableHead className="w-[160px]">워크스페이스</TableHead>
              <TableHead className="w-[90px] text-right">메시지</TableHead>
              <TableHead className="w-[110px] text-right">토큰</TableHead>
              <TableHead className="w-[170px]">마지막 대화</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((session) => (
              <TableRow
                key={session.sessionId}
                className="cursor-pointer hover:bg-muted/60"
                onClick={() => onOpenSession(session)}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onOpenSession(session);
                  }
                }}
                aria-label={`대화 보기 — ${session.title ?? "(제목 없음)"}`}
              >
                <TableCell>
                  <span className="font-medium text-sm line-clamp-1 max-w-[360px]">
                    {session.title || "(제목 없음)"}
                  </span>
                </TableCell>
                <TableCell>
                  <SessionTypeBadge type={session.sessionType} />
                </TableCell>
                <TableCell>
                  <span className="block text-sm text-muted-foreground truncate max-w-[150px]">
                    {session.workspaceName ?? "—"}
                  </span>
                </TableCell>
                <TableCell className="text-right text-sm tabular-nums">
                  {session.messageCount.toLocaleString()}
                </TableCell>
                <TableCell className="text-right text-sm tabular-nums text-muted-foreground">
                  {session.totalTokensUsed.toLocaleString()}
                </TableCell>
                <TableCell
                  className="text-xs text-muted-foreground whitespace-nowrap"
                  title={`생성: ${formatDateTime(session.createdAt)}`}
                >
                  {formatDateTime(session.lastMessageAt)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <p className="text-sm text-muted-foreground text-center md:text-left">
          {meta.totalCount.toLocaleString()}개 중{" "}
          {rangeStart.toLocaleString()} - {rangeEnd.toLocaleString()}
        </p>
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={pagination.prevPage}
            disabled={!meta.hasPrevPage}
            aria-label="이전 페이지"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm min-w-[60px] text-center">
            {meta.currentPage} / {meta.totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={pagination.nextPage}
            disabled={!meta.hasNextPage}
            aria-label="다음 페이지"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
